import React, { useEffect, useRef } from 'react';
import { array, bool, node, object, string } from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { logEvent } from '../../../util/tracking';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings,
    pagination,
    search,
    searchString,
    setActiveListing,
    isMapVariant,
    currentUser,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Intent tracking for the CRM: one event per distinct query, plus each result
  // opened. Best-effort; the server resolves the member from the session.
  const isLoggedIn = !!currentUser?.id?.uuid;
  const loggedSearch = useRef(null);

  useEffect(() => {
    const shownListingIds = (listings || []).map(l => l?.id?.uuid).filter(Boolean);
    if (!isLoggedIn || shownListingIds.length === 0 || loggedSearch.current === searchString) {
      return;
    }
    loggedSearch.current = searchString;
    logEvent({
      eventType: 'search',
      queryString: searchString,
      filters: search || {},
      shownListingIds,
      resultCount: pagination?.totalItems ?? shownListingIds.length,
    });
    // Keyed on the query, not the filter/pagination objects: those are new on every
    // render and would re-log the same search repeatedly.
  }, [searchString, listings, isLoggedIn]);

  const handleListingClick = listing => {
    if (!isLoggedIn) {
      return;
    }
    logEvent({
      eventType: 'searchResultClick',
      listingId: listing?.id?.uuid,
      queryString: searchString,
    });
  };

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="SearchPage"
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <ListingCard
            // className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
            currentUser={currentUser}
            searchString={searchString}
            onListingClick={handleListingClick}
          />
        ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

SearchResultsPanel.defaultProps = {
  children: null,
  className: null,
  listings: [],
  pagination: null,
  rootClassName: null,
  search: null,
  isMapVariant: true,
};

SearchResultsPanel.propTypes = {
  children: node,
  className: string,
  listings: array,
  pagination: propTypes.pagination,
  rootClassName: string,
  search: object,
  isMapVariant: bool,
};

export default SearchResultsPanel;
